import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Eyebrow } from "@/components/ui/editorial";
import { supabase } from "@/integrations/supabase/client";
import { useAttorneyAccountTier } from "@/hooks/useAttorneyAccountTier";

interface QuotaRow {
  label: string;
  used: number;
  limit: number;
}

interface FreeQuotaBannerProps {
  onItemClick?: () => void;
}

const FreeQuotaBanner: React.FC<FreeQuotaBannerProps> = ({ onItemClick }) => {
  const navigate = useNavigate();
  const { accountTier, hasPaidAccess, tierCaps, lawyerData, loading } = useAttorneyAccountTier();
  const [rows, setRows] = useState<QuotaRow[] | null>(null);

  const lawyerId = (lawyerData as any)?.id as string | undefined;
  const isFree = !loading && !hasPaidAccess && accountTier === "free";

  useEffect(() => {
    let cancelled = false;
    if (!isFree || !lawyerId) {
      setRows(null);
      return;
    }

    (async () => {
      const connectionLimit = tierCaps.monthly_connection_limit ?? 3;
      const caseLimit = tierCaps.case_limit ?? 3;
      const matterLimit = tierCaps.matter_limit ?? 3;

      const [{ data: lawyer }, caseQuota, matterQuota] = await Promise.all([
        supabase
          .from("lawyers")
          .select("monthly_connection_count, monthly_connection_period_start")
          .eq("id", lawyerId)
          .maybeSingle(),
        supabase.rpc("enforce_free_resource_quota", { p_lawyer_id: lawyerId, p_resource: "case" }),
        supabase.rpc("enforce_free_resource_quota", { p_lawyer_id: lawyerId, p_resource: "matter" }),
      ]);

      if (cancelled) return;

      const connectionsUsed = Math.max(0, Number(lawyer?.monthly_connection_count ?? 0));
      const caseRes = (caseQuota?.data ?? {}) as { used?: number; limit?: number };
      const matterRes = (matterQuota?.data ?? {}) as { used?: number; limit?: number };

      setRows([
        { label: "Connections / mo", used: connectionsUsed, limit: connectionLimit },
        { label: "Active cases", used: caseRes.used ?? 0, limit: caseRes.limit ?? caseLimit },
        { label: "Active matters", used: matterRes.used ?? 0, limit: matterRes.limit ?? matterLimit },
      ]);
    })();

    return () => {
      cancelled = true;
    };
  }, [isFree, lawyerId, tierCaps.case_limit, tierCaps.matter_limit, tierCaps.monthly_connection_limit]);

  if (!isFree) return null;

  const handleUpgrade = () => {
    onItemClick?.();
    navigate("/attorney-portal?tab=subscription");
  };

  return (
    <div className="px-6 pt-5 pb-4 border-b border-border bg-muted/20 space-y-3">
      <div className="flex items-center justify-between">
        <Eyebrow size="sm" tone="foreground">Free Plan Usage</Eyebrow>
        <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">This month</span>
      </div>

      <div className="space-y-2.5">
        {(rows ?? [
          { label: "Connections / mo", used: 0, limit: tierCaps.monthly_connection_limit ?? 3 },
          { label: "Active cases", used: 0, limit: tierCaps.case_limit ?? 3 },
          { label: "Active matters", used: 0, limit: tierCaps.matter_limit ?? 3 },
        ]).map((row) => {
          const remaining = Math.max(0, row.limit - row.used);
          const pct = row.limit > 0 ? Math.min(100, (row.used / row.limit) * 100) : 0;
          const reached = remaining === 0;
          return (
            <div key={row.label} className="space-y-1">
              <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.14em]">
                <span className="text-foreground/80">{row.label}</span>
                <span className={reached ? "text-destructive font-medium" : "text-muted-foreground"}>
                  {remaining} left · {row.used}/{row.limit}
                </span>
              </div>
              <Progress value={pct} className="h-1" />
            </div>
          );
        })}
      </div>

      <Button
        onClick={handleUpgrade}
        className="w-full rounded-none uppercase tracking-[0.16em] text-xs hover:bg-lexsavvy-yellow hover:text-foreground"
      >
        Upgrade Plan
      </Button>
    </div>
  );
};

export default FreeQuotaBanner;